import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type { ElementType } from 'react';

import { timeAgo } from '@/utils/dateUtils';

/**
 * The kind of notification, which also determines the body format:
 * `Message` bodies are HTML (typically pasted from Loops) and
 * `Alert` bodies are Markdown. Selecting `Alert` enables severity.
 */
export enum NotificationKind {
  Message = 'Message',
  Alert = 'Alert',
}

/** The category a notification belongs to. Shared by both kinds. */
export enum NotificationCategory {
  PlatformStatus = 'PlatformStatus',
  FeatureReleases = 'FeatureReleases',
  Billing = 'Billing',
  Security = 'Security',
  Integrations = 'Integrations',
  Maintenance = 'Maintenance',
  Support = 'Support',
}

/**
 * Severity for `NotificationKind.Alert` notifications, surfaced through the UI
 * colour system (row border, icon badge, pill). Null for `NotificationKind.Message`.
 */
export enum NotificationSeverity {
  Critical = 'Critical',
  Warning = 'Warning',
  Info = 'Info',
  Resolved = 'Resolved',
}

/** Optional call-to-action (a button or link) rendered with the notification. */
export interface CtaDto {
  label: string;
  url: string;
}

export interface VnMessageServiceBaseDto {
  id: string;
}

/**
 * The canonical notification content. A single message is the source of truth for every OU it is
 * delivered to (see `NotificationTargetDto`); editing it changes it immediately for all
 * recipients and no versioning is maintained.
 */
export interface NotificationMessageDto extends VnMessageServiceBaseDto {
  /** Id of the OU that administers this notification (admin list page is keyed on this). */
  ownerId: string;

  kind: NotificationKind;

  category: NotificationCategory;

  /** Alerts only; null for the Message kind. */
  severity?: NotificationSeverity | null;

  title: string;

  /** Short text shown in the feed list. */
  summary: string;

  /**
   * S3 object path to the body file (HTML for Message, Markdown for Alert). Server-generated on the
   * first upsert and derived from `id`; read-only from a client's perspective.
   */
  bodyFilePath: string;

  body: any | null; // resolved body data for preview

  cta?: CtaDto | null;

  /** Surface the notification as a full-screen takeover on next login. */
  showAsFullScreen: boolean;

  /** False in V1; reserved for future system-triggered alerts. */
  isSystemGenerated: boolean;

  /** Set on create (ISO-8601 when serialized). */
  createdDate: string | Date;

  /** Updated on every upsert (ISO-8601 when serialized). */
  modifiedDate: string | Date;

  sendDate?: string | Date;

  /** Epoch seconds; 90 days from the last modifiedDate, extended on upsert. Server-managed. */
  ttl: number;

  /**
   * Target OU ids this message is delivered to. Backend includes these on list-by-owner and get-by-id.
   * Empty means draft (not delivered). Not stored on the message document.
   */
  targetIds?: string[];
}

/**
 * Presigned S3 URLs for a notification's body file. Because the body path is deterministic from the
 * message id, these are always available for an existing message.
 */
export interface NotificationBodyUrlsDto {
  /** Presigned GET URL used by the UI to fetch and render the body. */
  readUrl: string;

  /** Presigned PUT URL used by the admin UI to upload or replace the body content. */
  writeUrl: string;

  /** UTC instant at which both URLs expire. */
  expiresAt: string | Date;
}

/**
 * A targeting record linking a `NotificationMessageDto` to a single target OU. One record
 * is written per (message, target OU) pair. The set of a message's targets defines who it is delivered
 * to; a message with no targets behaves as a draft.
 */
export interface NotificationTargetDto extends VnMessageServiceBaseDto {
  /** Id of the target OU the notification is delivered to. */
  targetId: string;

  /** Id of the related `NotificationMessageDto`. */
  notificationMessageId: string;

  /** Set on create (ISO-8601 when serialized). */
  createdDate: string | Date;

  /** Epoch seconds; 2 years from creation as a safety net against orphaned records. Server-managed. */
  ttl: number;
}

/**
 * Request body to replace the full set of target OUs for a message in a single call (mirrors the admin
 * "Target OUs" dropdown save). Targets not present are removed; new ones are added.
 */
export interface SetNotificationTargetsDto {
  targetIds: string[];
}

/**
 * ViewNexaMessagingService HTTP routes (relative to `notificationServiceUrl`).
 * Mirrors `NotificationMessageController` and `NotificationTargetController`.
 */
export const VnMessageServiceApi = {
  /** POST — create or update a notification message. */
  upsertNotification: 'notification',
  /** GET — single notification by id. */
  getNotification: (notificationId: string) => `notification/${notificationId}`,
  /** DELETE — delete a notification message. */
  deleteNotification: (notificationId: string) => `notification/${notificationId}`,
  /** GET — admin list: all notifications administered within an OU. */
  listByOwner: (ownerId: string) => `owner/${ownerId}/notification`,
  /** GET — user hub: notifications delivered to an OU. */
  getUserNotifications: (targetId: string) => `target/${targetId}/notification`,
  /** GET — presigned read/write URLs for a message body file. */
  getBodyUrls: (notificationId: string) => `notification/${notificationId}/body-urls`,
  /** GET — list target OUs a message is delivered to. */
  listTargets: (notificationId: string) => `notification/${notificationId}/target`,
  /** PUT — replace the full set of target OUs for a message. */
  setTargets: (notificationId: string) => `notification/${notificationId}/target`,
  /** POST — deliver a message to a target OU (idempotent). */
  addTarget: (notificationId: string, targetId: string) => `notification/${notificationId}/target/${targetId}`,
  /** DELETE — stop delivering a message to a target OU. */
  removeTarget: (notificationId: string, targetId: string) => `notification/${notificationId}/target/${targetId}`,
} as const;

/**
 * Upsert (create/update) payload for `POST /notification`. Metadata only; the body is uploaded
 * separately to S3 (see `NotificationBodyUrlsDto`) and targets are set via `SetNotificationTargetsDto`.
 *
 * Server-managed fields (`bodyFilePath`, `isSystemGenerated`, `createdDate`, `modifiedDate`, `ttl`) are
 * intentionally omitted: the backend is the source of truth and ignores any client-supplied values.
 *
 * Upsert semantics: omit `id` to create; include a known `id` to update; an unknown `id` is treated as
 * a create by the backend. `ownerId` is immutable after create.
 */
export interface NotificationMessageUpsertDto {
  /** Omit to create; include to update/upsert. */
  id?: string;

  /** Required. Immutable after create (changing it on update returns 400). */
  ownerId: string;

  /** Required. */
  title: string;

  /** Required. Determines body format and whether `severity` applies. */
  kind: NotificationKind;

  /** Required iff `kind === NotificationKind.Alert`; cleared/ignored by the backend for `Message`. */
  severity?: NotificationSeverity | null;

  category?: NotificationCategory;

  summary?: string;

  cta?: CtaDto | null;

  showAsFullScreen?: boolean;

  /** Optional scheduled send time (ISO-8601 when serialized). */
  sendDate?: string | Date;
}

// dto > ui ready shape >> lifecycle >> toSaveRequest >> CRUD service

//  ui ready shape form or populated dto

/** Form state for the admin create/edit page (react-hook-form). */
export type NotificationMessageForm = Omit<
  NotificationMessageDto,
  'bodyFilePath' | 'createdDate' | 'modifiedDate' | 'ttl' | 'isSystemGenerated' | 'cta'
> & {
  body: string;
  targetIds: string[];
  ctaLabel: string;
  ctaUrl: string;
};

export const NOTIFICATION_KIND_OPTIONS = [
  { value: NotificationKind.Message, label: 'Message' },
  { value: NotificationKind.Alert, label: 'Alert' },
];

export const NOTIFICATION_CATEGORY_OPTIONS = [
  { value: NotificationCategory.PlatformStatus, label: 'Platform Status', icon: MonitorHeartOutlinedIcon },
  { value: NotificationCategory.FeatureReleases, label: 'Feature Releases', icon: AutoAwesomeOutlinedIcon },
  { value: NotificationCategory.Billing, label: 'Billing', icon: CreditCardOutlinedIcon },
  { value: NotificationCategory.Security, label: 'Security', icon: ShieldOutlinedIcon },
  { value: NotificationCategory.Integrations, label: 'Integrations', icon: PowerOutlinedIcon },
  { value: NotificationCategory.Maintenance, label: 'Maintenance', icon: BuildOutlinedIcon },
  { value: NotificationCategory.Support, label: 'Support', icon: SupportOutlinedIcon },
];

const NOTIFICATION_CATEGORY_ICONS: Record<NotificationCategory, ElementType> = {
  [NotificationCategory.PlatformStatus]: MonitorHeartOutlinedIcon,
  [NotificationCategory.FeatureReleases]: AutoAwesomeOutlinedIcon,
  [NotificationCategory.Billing]: CreditCardOutlinedIcon,
  [NotificationCategory.Security]: ShieldOutlinedIcon,
  [NotificationCategory.Integrations]: PowerOutlinedIcon,
  [NotificationCategory.Maintenance]: BuildOutlinedIcon,
  [NotificationCategory.Support]: SupportOutlinedIcon,
};

/** Resolve the icon component that represents a notification's category. */
export function getNotificationCategoryIcon(category: NotificationCategory): ElementType {
  return NOTIFICATION_CATEGORY_ICONS[category];
}

export type NotificationSeverityTone = 'neutral' | 'info' | 'warning' | 'error' | 'success';

const NOTIFICATION_SEVERITY_ICONS: Record<NotificationSeverity, ElementType> = {
  [NotificationSeverity.Critical]: ErrorOutlineOutlinedIcon,
  [NotificationSeverity.Warning]: WarningAmberOutlinedIcon,
  [NotificationSeverity.Info]: InfoOutlinedIcon,
  [NotificationSeverity.Resolved]: CheckCircleOutlineOutlinedIcon,
};

export function getNotificationSeverityTone(severity?: NotificationSeverity | null): NotificationSeverityTone {
  switch (severity) {
    case NotificationSeverity.Critical:
      return 'error';
    case NotificationSeverity.Warning:
      return 'warning';
    case NotificationSeverity.Info:
      return 'info';
    case NotificationSeverity.Resolved:
      return 'success';
    default:
      return 'neutral';
  }
}

export function getNotificationSeverityIcon(severity?: NotificationSeverity | null): ElementType {
  if (!severity) {
    return WarningAmberOutlinedIcon;
  }
  return NOTIFICATION_SEVERITY_ICONS[severity] ?? WarningAmberOutlinedIcon;
}

/** Megaphone icon used for Message / Announcement surfaces. */
export function getNotificationAnnouncementIcon(): ElementType {
  return CampaignOutlinedIcon;
}

/** Icon for a notification kind: megaphone for Message, severity icon for Alert. */
export function getNotificationKindIcon(
  kind: NotificationKind,
  severity?: NotificationSeverity | null
): ElementType {
  if (kind === NotificationKind.Message) {
    return getNotificationAnnouncementIcon();
  }
  return getNotificationSeverityIcon(severity);
}

/** Header/list label: "Announcement" for Message, "{Severity} Alert" for Alert. */
export function getNotificationKindLabel(
  kind: NotificationKind,
  severity?: NotificationSeverity | null
): string {
  if (kind === NotificationKind.Message) {
    return 'Announcement';
  }
  return `${severity ?? NotificationSeverity.Warning} Alert`;
}

/** Takeover modal label: uppercase Figma copy (e.g. CRITICAL ISSUE, INFORMATION). */
export function getNotificationTakeoverLabel(
  kind: NotificationKind,
  severity?: NotificationSeverity | null
): string {
  if (kind === NotificationKind.Message) {
    return 'ANNOUNCEMENT';
  }
  switch (severity) {
    case NotificationSeverity.Critical:
      return 'CRITICAL ISSUE';
    case NotificationSeverity.Warning:
      return 'WARNING';
    case NotificationSeverity.Info:
      return 'INFORMATION';
    case NotificationSeverity.Resolved:
      return 'RESOLVED';
    default:
      return 'WARNING';
  }
}

/** CSS modifier key for takeover accent palette (severity or announcement). */
export function getNotificationTakeoverToneKey(
  kind: NotificationKind,
  severity?: NotificationSeverity | null
): string {
  if (kind === NotificationKind.Message) {
    return 'announcement';
  }
  switch (severity) {
    case NotificationSeverity.Critical:
      return 'critical';
    case NotificationSeverity.Warning:
      return 'warning';
    case NotificationSeverity.Info:
      return 'info';
    case NotificationSeverity.Resolved:
      return 'resolved';
    default:
      return 'warning';
  }
}

export const NOTIFICATION_SEVERITY_OPTIONS = [
  { value: NotificationSeverity.Info, label: 'Info' },
  { value: NotificationSeverity.Warning, label: 'Warning' },
  { value: NotificationSeverity.Critical, label: 'Critical' },
  { value: NotificationSeverity.Resolved, label: 'Resolved' },
];

export function getDefaultNotificationMessageForm(ownerId: string): NotificationMessageForm {
  return {
    id: '',
    ownerId,
    kind: NotificationKind.Message,
    category: NotificationCategory.FeatureReleases,
    severity: null,
    title: '',
    summary: '',
    body: '',
    ctaLabel: '',
    ctaUrl: '',
    showAsFullScreen: false,
    sendDate: undefined,
    targetIds: [],
  };
}

export function notificationMessageDtoToForm(
  dto: NotificationMessageDto,
  targetIds: string[] = [],
  body = ''
): NotificationMessageForm {
  return {
    id: dto.id,
    ownerId: dto.ownerId,
    kind: dto.kind,
    category: dto.category,
    severity: dto.severity ?? null,
    title: dto.title,
    summary: dto.summary,
    body,
    ctaLabel: dto.cta?.label ?? '',
    ctaUrl: dto.cta?.url ?? '',
    showAsFullScreen: dto.showAsFullScreen,
    sendDate: dto.sendDate,
    targetIds,
  };
}

export function notificationMessageFormToUpsertDto(form: NotificationMessageForm): NotificationMessageUpsertDto {
  const hasCta = Boolean(form.ctaLabel?.trim() && form.ctaUrl?.trim());

  return {
    id: form.id || undefined,
    ownerId: form.ownerId,
    kind: form.kind,
    category: form.category,
    severity: form.kind === NotificationKind.Alert ? form.severity ?? null : null,
    title: form.title,
    summary: form.summary,
    cta: hasCta ? { label: form.ctaLabel.trim(), url: form.ctaUrl.trim() } : null,
    showAsFullScreen: form.showAsFullScreen,
    sendDate: form.sendDate,
  };
}

/**
 * SendDate rule A: null/undefined = live now; otherwise live only once sendDate has arrived (`<= now`).
 */
export function isNotificationLiveBySendDate(
  sendDate: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  if (sendDate == null || sendDate === '') {
    return true;
  }
  const parsed = sendDate instanceof Date ? sendDate : new Date(sendDate);
  if (Number.isNaN(parsed.getTime())) {
    return true;
  }
  return parsed.getTime() <= now.getTime();
}

export function getNotificationAge(notification: NotificationMessageDto, now: Date = new Date()): number {
  const goLiveTime = notification.sendDate ? notification.sendDate : notification.createdDate;
  const difference = now.getTime() - new Date(goLiveTime).getTime();

  return difference;
}

export function getNotificationAgeText(notification: NotificationMessageDto, now: Date = new Date()): string {
  return timeAgo(getNotificationAge(notification, now));
}

/** Build a preview-ready DTO from admin form state (body populated from the editor, no S3 fetch). */
export function notificationMessageFormToPreviewDto(
  form: NotificationMessageForm,
  ownerId: string
): NotificationMessageDto {
  const hasCta = Boolean(form.ctaLabel?.trim() && form.ctaUrl?.trim());
  const now = new Date().toISOString();

  return {
    id: form.id || 'preview-draft',
    ownerId,
    kind: form.kind,
    category: form.category,
    severity: form.kind === NotificationKind.Alert ? form.severity ?? null : null,
    title: form.title,
    summary: form.summary,
    bodyFilePath: '',
    body: form.body ?? '',
    cta: hasCta ? { label: form.ctaLabel.trim(), url: form.ctaUrl.trim() } : null,
    showAsFullScreen: form.showAsFullScreen,
    isSystemGenerated: false,
    createdDate: now,
    modifiedDate: now,
    sendDate: form.sendDate,
    ttl: 0,
    targetIds: form.targetIds ?? [],
  };
}
